// ═══════════════════════════════════════════════════════════════════════════
// AGENT-BRIDGE-BROWSER — session quad / role / epoch tracker (ABB-S3)
// ═══════════════════════════════════════════════════════════════════════════
//
// The authoritative ownership-state machine extracted from agentBridgeClient.ts
// (the 500-line rule). Tracks the cached `welcome` quad + role + current epoch +
// the latest broadcast epoch, and derives "am I the current active epoch"
// (SM-12 / SM-15 demotion gating, consumed by S4/S5). The client drives it:
//   • adoptWelcome(frame) — a fresh/unsolicited welcome re-keys us (idempotent
//     for the same epoch). Accepted at ANY time post-pairing.
//   • applySessionChanged(frame) — a broadcast: a HIGHER epoch demotes us; the
//     SAME epoch is idempotent (the auto-promotion welcome+session-changed pair).
//   • reset() — drop/disconnect clears all state (no resume, INV-5).
//
// The `onRoleChange` callback fires whenever role/epoch may have changed so S5's
// demotion gating re-evaluates. LEAF module: imports ONLY the codec types.
// ═══════════════════════════════════════════════════════════════════════════

import type {
  BridgeRole,
  InboundSessionChangedFrame,
  InboundWelcomeFrame,
  SessionQuad,
} from "./wsFrameCodec";

/** Fired whenever role/epoch may have changed (S5 demotion gating re-evaluates). */
export type RoleChangeCallback = (role: BridgeRole | null, sessionEpoch: number | null) => void;

/** Tracks the authoritative ownership quad/role/epoch + demotion state. */
export class SessionTracker {
  private readonly onRoleChange: RoleChangeCallback | undefined;

  /** The cached authoritative quad from the latest welcome (null until paired). */
  private quad: SessionQuad | null = null;

  /** The current seat role (null until the first welcome). */
  private role: BridgeRole | null = null;

  /** The ownership epoch the browser stamps on sent frames (null until welcome). */
  private currentEpoch: number | null = null;

  /** The latest broadcast epoch seen via session-changed (for demotion detection). */
  private latestBroadcastEpoch: number | null = null;

  constructor(onRoleChange?: RoleChangeCallback) {
    this.onRoleChange = onRoleChange;
  }

  /** The current seat role, or null if not paired. */
  getRole(): BridgeRole | null {
    return this.role;
  }

  /** The current ownership epoch, or null. */
  getCurrentEpoch(): number | null {
    return this.currentEpoch;
  }

  /** A defensive copy of the cached quad, or null. */
  getQuad(): SessionQuad | null {
    return this.quad === null ? null : { ...this.quad };
  }

  /**
   * Whether I am the CURRENT ACTIVE epoch (S4/S5 gate execution + pushes on this).
   * True only when paired active AND no later broadcast epoch superseded my
   * welcomed epoch (a `session-changed` with a higher epoch = demoted).
   */
  isCurrentActiveEpoch(): boolean {
    if (this.role !== "active" || this.currentEpoch === null) {
      return false;
    }
    if (this.latestBroadcastEpoch !== null && this.latestBroadcastEpoch > this.currentEpoch) {
      return false; // a newer broadcast epoch superseded me → demoted.
    }
    return true;
  }

  /**
   * Adopt a `welcome` as the authoritative ownership quad/role/epoch. Accepted at
   * ANY time post-pairing (an unsolicited welcome{epoch+1} after our document-
   * changed re-keys us — SM-12). Idempotent for the same epoch.
   *
   * [P3 monotonic] IGNORE a welcome whose epoch is BELOW our current epoch: the
   * live wire is monotonic (INV-7), but the codec treats it as untrusted, so a
   * stale/replayed lower-epoch welcome must never regress the epoch / un-demote
   * us. A welcome equal-to or above the current epoch is adopted (a re-key or an
   * idempotent same-epoch welcome).
   *
   * [round-3 P2 #1] RETURNS whether the welcome was ADOPTED (`false` on the stale
   * lower-epoch early-return, `true` on the adopt path) so the client gates its
   * own status / role / doc-refresh side-effects on the TRACKER authority — a
   * stale lower-epoch welcome must not flip a demoted observer back to
   * `connected-active` off the RAW frame role, nor trigger a spurious
   * `document-changed`. The existing tracker tests assert STATE, not the return
   * value, so they stay green.
   */
  adoptWelcome(frame: InboundWelcomeFrame): boolean {
    if (this.currentEpoch !== null && frame.sessionEpoch < this.currentEpoch) {
      return false; // stale/replayed lower-epoch welcome → ignore (never regress).
    }
    this.quad = {
      sessionId: frame.sessionId,
      sessionEpoch: frame.sessionEpoch,
      bootId: frame.bootId,
      documentId: frame.documentId,
      documentEpoch: frame.documentEpoch,
    };
    this.currentEpoch = frame.sessionEpoch;
    // [round-4 P2 #3] DERIVE the role consistently with the demotion high-water — do
    // NOT trust `frame.role` blindly. A replayed/superseded welcome whose epoch is at
    // or below a NEWER broadcast high-water (`latestBroadcastEpoch > frame.sessionEpoch`)
    // does NOT re-promote us: a later `session-changed{N+2}` already demoted us, and a
    // stale (round-3 the epoch-EQUAL / below-high-water case slipped through) welcome at
    // epoch N must NOT restore `role='active'` while `isCurrentActiveEpoch()` stays false
    // (it would, because the high-water still exceeds the welcomed epoch). Force observer
    // in that case so getRole(), isCurrentActiveEpoch(), and onRoleChange ALL agree.
    // Only an `active` welcome whose epoch is ≥ the broadcast high-water is adopted as
    // active (a genuine fresh promotion that the wire has not yet superseded).
    const supersededByNewerBroadcast =
      this.latestBroadcastEpoch !== null && this.latestBroadcastEpoch > frame.sessionEpoch;
    this.role = supersededByNewerBroadcast ? "observer" : frame.role;
    // A fresh welcome epoch is authoritative — never LOWER the broadcast high-water
    // (monotonic): take the max so a later replayed older session-changed cannot
    // un-demote us, and an adopted higher welcome epoch advances it.
    this.latestBroadcastEpoch =
      this.latestBroadcastEpoch === null
        ? frame.sessionEpoch
        : Math.max(this.latestBroadcastEpoch, frame.sessionEpoch);
    this.onRoleChange?.(this.role, this.currentEpoch);
    return true; // [round-3 P2 #1] adopted → the client may run its side-effects.
  }

  /**
   * Apply a `session-changed` broadcast. A HIGHER epoch demotes me
   * (isCurrentActiveEpoch() flips false AND `role` is set to `observer` so the
   * public role never lies about a demotion — [P2 role-demote]). The SAME epoch is
   * idempotent (the auto-promotion welcome+session-changed pair) — we adopt the
   * sessionId defensively in case the broadcast led the welcome, but never mutate
   * role/epoch (only a `welcome` carries the full quad + re-keys).
   *
   * [P3 monotonic] The broadcast high-water never LOWERS: a stale/replayed
   * lower-epoch broadcast must not un-demote us, so take `Math.max`.
   *
   * [round-3 P2 #2] PRE-WELCOME DROP: a `session-changed` received before any
   * welcome paired us (`currentEpoch === null`) is INERT — it must NOT seed
   * `latestBroadcastEpoch`, else a phantom high-water would wrongly demote a later
   * LEGITIMATE welcome at epoch ≤ phantom (isCurrentActiveEpoch() false). This is
   * the sibling of the client's onRpc / onExportControl pre-welcome epoch-drop
   * (both inert when `getCurrentEpoch() === null`). The companion never broadcasts
   * `session-changed` to an UNPAIRED socket (`wsServer.ts` broadcastSessionChanged
   * filters on `state.paired`), so a pre-welcome broadcast only arrives on an
   * untrusted / reordered wire — exactly this module's threat model.
   */
  applySessionChanged(frame: InboundSessionChangedFrame): void {
    if (this.currentEpoch === null) {
      return; // [round-3 P2 #2] pre-welcome broadcast → never seed the high-water.
    }
    this.latestBroadcastEpoch =
      this.latestBroadcastEpoch === null
        ? frame.sessionEpoch
        : Math.max(this.latestBroadcastEpoch, frame.sessionEpoch);
    if (this.quad !== null && frame.sessionEpoch === this.currentEpoch) {
      this.quad = { ...this.quad, sessionId: frame.sessionId };
    }
    // [P2 role-demote] A broadcast epoch ABOVE my welcomed epoch means another tab
    // superseded me — demote the PUBLIC role to observer so `getRole()` agrees with
    // `isCurrentActiveEpoch()` (which the demotion already flipped false). The
    // welcomed epoch stays put (only a fresh welcome re-keys); only the role lowers.
    if (
      this.role === "active" &&
      this.currentEpoch !== null &&
      this.latestBroadcastEpoch > this.currentEpoch
    ) {
      this.role = "observer";
    }
    this.onRoleChange?.(this.role, this.currentEpoch);
  }

  /** Clear all session state (drop / disconnect — no resume, INV-5). */
  reset(): void {
    this.quad = null;
    this.role = null;
    this.currentEpoch = null;
    this.latestBroadcastEpoch = null;
    this.onRoleChange?.(null, null);
  }
}
